import React, { useEffect, useState } from "react";
import { Check, MessageSquare, Sparkles, Loader2, Send, X, Plus, Zap } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PageContainer, PageHeader, EmptyState } from "@/components/Page";

const features = [
  "Real Twilio SMS — your number, your contacts",
  "Bulk send to a phone list (E.164 format)",
  "AI-written campaign copy (3 variants ≤160 chars)",
  "Automatic STOP/HELP compliance",
  "Delivery tracking via Twilio SID + sms_log audit trail",
];

const tones = ["Friendly", "Urgent", "Playful", "Professional"];
const segments = ["All subscribers", "VIPs", "New leads", "Lapsed customers"];

const SMSMarketing = () => {
  const [data, setData] = useState({ enabled: false, campaigns: [] });
  const [show, setShow] = useState(false);
  const [sendCamp, setSendCamp] = useState(null); // campaign object being sent
  const [sendPhones, setSendPhones] = useState("");
  const [bulkSending, setBulkSending] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  // Composer
  const [c, setC] = useState({ name: "", topic: "", tone: "Friendly", segment: "All subscribers", message: "", include_link: true });
  const [variants, setVariants] = useState([]);
  const [gen, setGen] = useState(false);
  const [testPhone, setTestPhone] = useState("");
  const [sendingTest, setSendingTest] = useState(false);

  const load = () => api.get("/sms/dashboard").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const enable = async () => {
    const { data } = await api.post("/sms/enable");
    toast.success(data.live ? "SMS enabled — Twilio LIVE" : "SMS enabled (mocked, no Twilio creds)");
    load();
  };

  const closeModal = () => {
    setShow(false);
    setC({ name: "", topic: "", tone: "Friendly", segment: "All subscribers", message: "", include_link: true });
    setVariants([]); setTestPhone("");
  };

  const generate = async () => {
    if (!c.topic.trim()) return toast.error("Add a topic first");
    setGen(true);
    try {
      const { data } = await api.post("/sms/generate-message", { topic: c.topic, tone: c.tone.toLowerCase(), include_link: c.include_link });
      setVariants(data.messages || []);
    } catch { toast.error("Generate failed"); }
    finally { setGen(false); }
  };

  const sendTest = async () => {
    if (!testPhone || !c.message) return toast.error("Need phone + message");
    setSendingTest(true);
    try {
      await api.post("/sms/send-test", { message: c.message, to_phone: testPhone });
      toast.success(`Test SMS sent to ${testPhone} (MOCKED)`);
    } catch { toast.error("Send failed"); }
    finally { setSendingTest(false); }
  };

  const save = async () => {
    if (!c.name || !c.message) return toast.error("Need name + message");
    await api.post("/sms/campaign", { name: c.name, body: c.message, type: "sms" });
    toast.success("SMS campaign saved as draft");
    closeModal(); load();
  };

  const sendBulk = async () => {
    if (!sendCamp) return;
    const phones = sendPhones.split(/[\n,]/).map((p) => p.trim()).filter(Boolean);
    if (phones.length === 0) return toast.error("Add at least one phone number");
    const bad = phones.filter((p) => !p.startsWith("+"));
    if (bad.length) return toast.error(`All phones must start with + (E.164). Bad: ${bad.slice(0, 2).join(", ")}`);
    if (!confirm(`Send REAL SMS to ${phones.length} number${phones.length > 1 ? "s" : ""}? Each costs ~$0.008 via Twilio.`)) return;
    setBulkSending(true); setBulkResult(null);
    try {
      const { data } = await api.post("/sms/send-campaign", { campaign_id: sendCamp.id, to_phones: phones });
      setBulkResult(data);
      toast.success(`Sent ${data.sent}/${data.sent + data.failed}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setBulkSending(false); }
  };

  const closeSend = () => { setSendCamp(null); setSendPhones(""); setBulkResult(null); };

  if (!data.enabled) {
    return (
      <PageContainer>
        <PageHeader title="SMS Marketing" subtitle="Reach customers in their inbox." testId="sms-header" />
        <div className="max-w-xl mx-auto op-card p-8" data-testid="sms-enable-card">
          <div className="w-14 h-14 rounded-xl bg-[#EDE9FE] flex items-center justify-center mb-5"><MessageSquare className="text-[#7C3AED]" size={26} /></div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Enable SMS Marketing</h2>
          <p className="text-gray-500 mb-6">{data.live ? "Twilio is connected and ready. Activate to start sending real SMS." : "Powered by Twilio. Add credentials in backend/.env to send real SMS."}</p>
          {data.live && <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-5">● Twilio LIVE — your messages will actually send. ~$0.008/SMS.</p>}
          <ul className="space-y-2 mb-7">
            {features.map((f) => (
              <li key={f} className="flex items-start gap-2 text-sm text-gray-700"><Check className="text-emerald-500 mt-0.5 flex-shrink-0" size={16} /> {f}</li>
            ))}
          </ul>
          <button onClick={enable} className="op-btn-primary" data-testid="sms-enable-btn">Enable SMS →</button>
        </div>
      </PageContainer>
    );
  }

  const charCount = c.message.length;
  const overLimit = charCount > 160;

  return (
    <PageContainer>
      <PageHeader title="SMS Marketing" subtitle="Reach customers instantly." testId="sms-header"
        actions={
          <div className="flex items-center gap-2">
            <span className={`text-xs px-2 py-1 rounded-full font-semibold ${data.live ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"}`} data-testid="sms-live-badge">
              {data.live ? "● Twilio Live" : "○ Mocked"}
            </span>
            <button onClick={() => setShow(true)} className="op-btn-primary flex items-center gap-1.5" data-testid="sms-new"><Plus size={16} /> New SMS</button>
          </div>
        } />

      {data.campaigns.length === 0 ? (
        <div className="op-card"><EmptyState icon={MessageSquare} title="No SMS campaigns yet" description="Compose your first text campaign." action={<button onClick={() => setShow(true)} className="op-btn-primary">Compose →</button>} /></div>
      ) : (
        <div className="op-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50"><tr className="text-left text-xs font-semibold text-gray-600 uppercase"><th className="px-5 py-3">Name</th><th className="px-5 py-3">Message</th><th className="px-5 py-3">Status</th><th className="px-5 py-3">Created</th><th className="px-5 py-3"></th></tr></thead>
            <tbody>{data.campaigns.map((cp) => (
              <tr key={cp.id} className="border-t border-gray-100">
                <td className="px-5 py-3 font-medium">{cp.name}</td>
                <td className="px-5 py-3 text-gray-600 max-w-xs truncate">{cp.body}</td>
                <td className="px-5 py-3 capitalize"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${cp.status === "sent" ? "bg-emerald-100 text-emerald-700" : cp.status === "failed" ? "bg-red-100 text-red-700" : "bg-gray-100 text-gray-700"}`}>{cp.status}</span></td>
                <td className="px-5 py-3 text-gray-500 text-xs">{new Date(cp.created_at).toLocaleDateString()}</td>
                <td className="px-5 py-3">
                  {cp.status === "draft" && (
                    <button onClick={() => setSendCamp(cp)} className="op-btn-primary text-xs py-1.5 px-3 flex items-center gap-1" data-testid={`sms-send-${cp.id}`}>
                      <Zap size={12} /> Send
                    </button>
                  )}
                </td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}

      {show && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={closeModal} data-testid="sms-composer">
          <div className="bg-white rounded-2xl max-w-2xl w-full p-6 shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">New SMS Campaign</h2>
              <button onClick={closeModal}><X size={18} className="text-gray-400" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-semibold text-gray-700">Campaign name</label>
                <input value={c.name} onChange={(e) => setC({ ...c, name: e.target.value })} className="op-input mt-1" placeholder="Friday flash sale" data-testid="sms-name" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-700">Recipients</label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {segments.map((s) => (
                    <button key={s} onClick={() => setC({ ...c, segment: s })} className={c.segment === s ? "op-pill-active" : "op-pill-inactive"} data-testid={`sms-seg-${s.replace(/\s/g,'-').toLowerCase()}`}>{s}</button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-700">Topic</label>
                <input value={c.topic} onChange={(e) => setC({ ...c, topic: e.target.value })} className="op-input mt-1" placeholder="Friday 25% flash sale" data-testid="sms-topic" />
              </div>
              <div className="flex items-center justify-between gap-4">
                <div className="flex-1">
                  <label className="text-xs font-semibold text-gray-700">Tone</label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {tones.map((t) => (
                      <button key={t} onClick={() => setC({ ...c, tone: t })} className={c.tone === t ? "op-pill-active" : "op-pill-inactive"}>{t}</button>
                    ))}
                  </div>
                </div>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={c.include_link} onChange={(e) => setC({ ...c, include_link: e.target.checked })} data-testid="sms-include-link" />
                  Include link
                </label>
              </div>
              <button onClick={generate} disabled={gen || !c.topic} className="op-btn-primary w-full flex items-center justify-center gap-2" data-testid="sms-gen">
                {gen ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Generate 3 variants
              </button>
              {variants.length > 0 && (
                <div className="space-y-2">
                  {variants.map((v, i) => (
                    <button key={i} onClick={() => setC({ ...c, message: v })} className="w-full text-left text-sm px-3 py-2.5 border border-gray-200 rounded-lg hover:bg-purple-50 hover:border-[#7C3AED] flex items-start gap-2" data-testid={`sms-variant-${i}`}>
                      <Check size={14} className={`mt-0.5 ${c.message === v ? "text-[#7C3AED]" : "text-transparent"}`} />
                      <div className="flex-1">
                        <p>{v}</p>
                        <p className="text-xs text-gray-400 mt-1">{v.length} chars</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-semibold text-gray-700">Message</label>
                  <span className={`text-xs ${overLimit ? "text-red-500" : "text-gray-400"}`}>{charCount}/160</span>
                </div>
                <textarea rows={3} value={c.message} onChange={(e) => setC({ ...c, message: e.target.value })} className="op-input font-mono text-sm" placeholder="Pick a variant or type your own…" data-testid="sms-message" />
                <p className="text-xs text-gray-400 mt-1">STOP/HELP compliance auto-appended at send.</p>
              </div>
              <div className="border-t border-gray-100 pt-4">
                <label className="text-xs font-semibold text-gray-700">Send test SMS</label>
                <div className="flex gap-2 mt-1">
                  <input value={testPhone} onChange={(e) => setTestPhone(e.target.value)} type="tel" className="op-input flex-1" placeholder="+1 555 0100" data-testid="sms-test-phone" />
                  <button onClick={sendTest} disabled={sendingTest || !testPhone || !c.message} className="op-btn-secondary flex items-center gap-1" data-testid="sms-send-test">
                    {sendingTest ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send test
                  </button>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-gray-100">
              <button onClick={closeModal} className="op-btn-secondary">Cancel</button>
              <button onClick={save} disabled={!c.name || !c.message || overLimit} className="op-btn-primary" data-testid="sms-save">Save as draft →</button>
            </div>
          </div>
        </div>
      )}

      {sendCamp && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={closeSend} data-testid="sms-send-modal">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold flex items-center gap-2"><Zap size={18} className="text-[#7C3AED]" /> Send "{sendCamp.name}"</h2>
              <button onClick={closeSend}><X size={18} className="text-gray-400" /></button>
            </div>
            {!bulkResult ? (
              <>
                <div className="bg-gray-50 rounded-lg p-3 mb-4 text-sm font-mono text-gray-700">{sendCamp.body}</div>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-xs text-amber-800">
                  ⚠️ Real Twilio SMS at ~$0.008/message. Trial accounts can ONLY send to verified Twilio numbers.
                </div>
                <label className="text-xs font-semibold text-gray-700">Recipient phone numbers (E.164, one per line or comma-separated)</label>
                <textarea value={sendPhones} onChange={(e) => setSendPhones(e.target.value)} rows={5} className="op-input font-mono text-xs mt-1" placeholder="+15551234567&#10;+15551234568" data-testid="sms-bulk-phones" />
                <p className="text-xs text-gray-400 mt-1">Each number must start with `+` and country code.</p>
                <div className="flex justify-end gap-2 mt-5">
                  <button onClick={closeSend} className="op-btn-secondary" data-testid="sms-bulk-cancel">Cancel</button>
                  <button onClick={sendBulk} disabled={bulkSending || !sendPhones.trim()} className="op-btn-primary flex items-center gap-2" data-testid="sms-bulk-send">
                    {bulkSending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                    {bulkSending ? "Sending..." : "Send now"}
                  </button>
                </div>
              </>
            ) : (
              <div data-testid="sms-bulk-result">
                <div className="text-center py-3 mb-4">
                  <div className="w-14 h-14 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-2"><Check size={26} className="text-emerald-600" /></div>
                  <p className="text-2xl font-bold">{bulkResult.sent}/{bulkResult.sent + bulkResult.failed}</p>
                  <p className="text-sm text-gray-500">messages sent successfully</p>
                </div>
                {bulkResult.errors?.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                    <p className="text-xs font-semibold text-red-700 mb-2">Failures:</p>
                    <ul className="text-xs text-red-600 space-y-1">
                      {bulkResult.errors.map((e, i) => <li key={i}>{e.to}: {e.error}</li>)}
                    </ul>
                  </div>
                )}
                <button onClick={closeSend} className="op-btn-primary w-full">Done</button>
              </div>
            )}
          </div>
        </div>
      )}
    </PageContainer>
  );
};
export default SMSMarketing;
